import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('PAGE_COMMANDS');

export const pageCreateCommand: SelfContainedCommand = {
  id: 'page_create',
  description: '📄 CREATE: Create a new Page — a rich Notion-style document with tasks, action buttons, and entity embeds',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Title for the page (default: "Untitled Page")',
        required: false,
        defaultValue: 'Untitled Page',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID (uses current space if not provided)',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder ID (root level if not provided)',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'Gmail Setup Guide' },
        expectedOutput: createSuccessResult('Page created: Gmail Setup Guide', { entityId: 'page_abc123' }),
        description: 'Create a setup guide page',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { name = 'Untitled Page', spaceId: providedSpaceId, folderId } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.info('[page_create] Creating page:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newPage = await entityStore.actions.createEntity({
        entityType: 'Page',
        name: name.trim() || 'Untitled Page',
        spaceId,
        skeleton: {
          '@type': 'Page',
          version: '2.0',
          architecture: 'base-application',
          editor: 'blocknote',
          viewMode: 'block',
          base64: '',
          groupIds: folderId ? [folderId] : [],
        },
      });

      const entityId = newPage._id || newPage.id;

      log.info('[page_create] Page created:', entityId);

      return createSuccessResult(`Page created: ${name}`, {
        entityId,
        name: newPage.name,
        entityType: 'Page',
      });
    } catch (error) {
      log.error('[page_create] Failed:', error);
      return createErrorResult(
        'Failed to create page',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

entityCreationRegistry.register({
  commandId: 'page_create',
  entityType: 'Page',
  displayName: 'Page',
  icon: { type: 'phosphor', phosphorIcon: 'Article', phosphorWeight: 'duotone' },
  description: 'Rich page with tasks, buttons, and embeds',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'document',
  sortOrder: 15,
});
